// @ts-nocheck
import { Notifications, NotificationsType } from "@/integration/application/interfaces/notification.interface";

/**
 * Renamed and improved interface for invitation action parameters
 */
export interface INotificationActionParams {
  // General parameters
  contextType?: string;
  teamId: string;

  // Schedule invite parameters
  scheduleId: string;
  checkinId: string;
  isConfirmed: boolean;

  // Team invite parameters
  inviteId: string;
}

/**
 * Type guard for checking if a notification is a team invitation
 */
export const isTeamInvite = (notification: Notifications): boolean => {
  return (
    notification.context_type === NotificationsType.TEAM_INVITE || 
    notification.type === NotificationsType.TEAM_INVITE
  );
};

/**
 * Type guard for checking if a notification is a schedule invitation
 */
export const isScheduleInvite = (notification: Notifications): boolean => {

  const scheduleType = [NotificationsType.SCHEDULE_INVITE]

  return scheduleType.includes(notification.context_type);
};

/**
 * Type guard for checking if a notification is a team join notification
 */
export const isTeamJoin = (notification: Notifications): boolean => {
  return (
    notification.context_type === NotificationsType.TEAM_JOIN || 
    notification.type === NotificationsType.TEAM_JOIN
  );
};

/**
 * Type guard for checking if a notification has a pending status
 */
export const isPendingInvitation = (notification: Notifications): boolean => {
  if (typeof notification.body === 'object' && 'status' in notification.body) {
    return notification.body.status === 'PENDING';
  }
  return false;
};

/**
 * Type guard for checking if a notification requires action buttons
 */
export const shouldShowActions = (notification: Notifications): boolean => {
  // For object body notifications
  if (typeof notification.body === 'object') {
    return (
      isPendingInvitation(notification) && 
      (isTeamInvite(notification) || isScheduleInvite(notification))
    );
  }

  // For string array body notifications
  if (Array.isArray(notification.body)) {
    return (
      (isTeamInvite(notification) || isScheduleInvite(notification)) && 
      !notification.isRead
    );
  }

  return false;
};

/**
 * Extract parameters for invitation actions based on notification type and body format
 */
export const extractActionParams = (
  notification: Notifications, 
  isAccepting: boolean
): INotificationActionParams => {
  const contextType = typeof notification.body === 'object' 
    ? notification.body.context_type || notification.context_type
    : notification.context_type;

  // For object body
  if (typeof notification.body === 'object') {
    // Special handling for SCHEDULE_INVITE
    if (isScheduleInvite(notification)) {
      return {
        teamId: notification.body.schedule?.team_id || notification.id,
        inviteId: notification?.body.id || notification.id,
        contextType,
        scheduleId: notification.body.schedule_id || notification.id,
        checkinId: notification.body.id || notification.id,
        isConfirmed: isAccepting
      };
    }

    // If body has team_id and id properties
    if (notification.body.team_id && notification.body.id) {
      return {
        teamId: notification.body.team_id,
        inviteId: notification.body.id,
        contextType,
        scheduleId: "",
        checkinId: "",
        isConfirmed: isAccepting
      };
    }

    // If body has team property with id
    if (notification.body.team?.id) {
      return {
        teamId: notification.body.team.id,
        inviteId: notification.body.id || notification.id,
        contextType,
        scheduleId: "",
        checkinId: "",
        isConfirmed: isAccepting
      };
    }
  }

  // For string array body or fallback
  return {
    teamId: notification.id,
    inviteId: notification.id,
    contextType,
    scheduleId: isScheduleInvite(notification) ? notification.id : "",
    checkinId: isScheduleInvite(notification) ? notification.id : "",
    isConfirmed: isAccepting
  };
};
